import * as React from "react";
import { cn } from "@/lib/utils";

/** TB brand PLP Filters — a rail of filter chips, each one opening a panel of its own. */
export const PlpFilterBar = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    /** Classes for the inner rail, e.g. a page gutter in place of the DS's fixed 28px inset. */
    railClassName?: string;
  }
>(
  ({ className, railClassName, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "sticky top-0 z-[5] border-b border-[var(--tb-hairline)] bg-[rgb(255_255_255/0.94)] [backdrop-filter:blur(10px)]",
        className,
      )}
    >
      <div
        className={cn(
          "flex gap-2.5 overflow-x-auto px-7 py-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          railClassName,
        )}
        {...props}
      />
    </div>
  ),
);
PlpFilterBar.displayName = "PlpFilterBar";

export interface FilterChipProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  open?: boolean;
  /** Count of selected options. */
  count?: React.ReactNode;
}

export const FilterChip = React.forwardRef<HTMLButtonElement, FilterChipProps>(
  ({ className, active, open, count, children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-expanded={open}
      className={cn(
        "inline-flex shrink-0 cursor-pointer items-center gap-[7px] whitespace-nowrap rounded-full border px-[15px] py-[9px] text-[12.5px] text-[var(--fg)] transition-colors",
        active
          ? "border-tb-green bg-[var(--tb-green-tint)]"
          : "border-[var(--tb-border)] bg-tb-white hover:border-[var(--tb-border-strong)]",
        className,
      )}
      {...props}
    >
      {children}
      {count != null && <span className="font-mono text-[11px] text-[var(--fg-subtle)]">{count}</span>}
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        className={cn("h-3.5 w-3.5 text-[var(--fg-muted)] transition-transform", open && "rotate-180")}
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </button>
  ),
);
FilterChip.displayName = "FilterChip";

export const FilterClear = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, children = "Limpar filtros", ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn("shrink-0 cursor-pointer px-3.5 py-[9px] text-xs text-tb-green", className)}
    {...props}
  >
    <span className="border-b border-current">{children}</span>
  </button>
));
FilterClear.displayName = "FilterClear";

export interface FilterOptionProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  swatch?: React.ReactNode;
  count?: React.ReactNode;
}

/** Option row inside a filter panel: checkbox, label and count. */
export const FilterOption = ({ className, swatch, count, children, ...props }: FilterOptionProps) => (
  <label
    className={cn(
      "flex cursor-pointer items-center gap-2.5 rounded-[var(--tb-radius-xs)] px-1.5 py-2 text-[13px] text-[var(--fg)] hover:bg-[var(--tb-porcelain-tint)]",
      className,
    )}
    {...props}
  >
    <input type="checkbox" className="h-[15px] w-[15px] accent-tb-green" />
    {swatch != null && (
      <span className="h-4 w-4 shrink-0 rounded-full shadow-[inset_0_0_0_1px_rgb(61_63_57/0.16)]">{swatch}</span>
    )}
    <span className="flex-1">{children}</span>
    {count != null && <span className="font-mono text-xs text-[var(--fg-subtle)]">{count}</span>}
  </label>
);
FilterOption.displayName = "FilterOption";
